import { Box, VStack, HStack } from '@terra/ui-styled-system';
import { Text } from '../../../../components/styled/text';
import { Badge } from '../../../../components/badge';
import { MarkdownPreview } from '../../../markdown-preview';
import { CancelCircleIcon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';

type BrowserRendererProps = {
  data:
    | {
        result?: string;
        logs?: Record<string, unknown> | string;
        success?: boolean;
        status?: string;
      }
    | string;
  maxHeight?: string;
};
type ParsedBrowserOutput = {
  success?: boolean;
  status?: string;
  result?: string;
  logs?: Record<string, unknown> | string;
};

function parseBrowserOutput(output: string): ParsedBrowserOutput | null {
  try {
    const parsed = JSON.parse(output);

    if (typeof parsed === 'object' && parsed !== null) {
      return parsed as ParsedBrowserOutput;
    }
  } catch {
    // Not JSON, return null
  }

  return null;
}

function hasLogs(logs?: Record<string, unknown> | string): boolean {
  if (!logs) return false;
  if (typeof logs === 'string') return logs.trim().length > 0;
  if (typeof logs === 'object') {
    return (
      Object.keys(logs).length > 0 &&
      Object.values(logs).some((v) => v !== null && v !== undefined && v !== '')
    );
  }

  return false;
}

export function BrowserRenderer({
  data,
  maxHeight = '300px',
}: BrowserRendererProps) {
  const t = useTranslations('designSystem.chat');
  let parsedOutput: ParsedBrowserOutput | null = null;

  if (typeof data === 'string') {
    parsedOutput = parseBrowserOutput(data);
  } else if (typeof data === 'object') {
    parsedOutput = data as ParsedBrowserOutput;
  }

  if (!parsedOutput) {
    return (
      <Box
        bg="bg.canvas"
        p={2}
        borderRadius="sm"
        borderWidth="1px"
        borderColor="border.subtle"
        width="100%"
        maxHeight={maxHeight}
        overflow="auto"
      >
        <Text fontSize="xs" fontFamily="mono" whiteSpace="pre-wrap">
          {typeof data === 'string' ? data : JSON.stringify(data, null, 2)}
        </Text>
      </Box>
    );
  }

  const { success, status, result, logs } = parsedOutput;
  const isError =
    success === false || (status && status.toLowerCase() !== 'success');
  const showLogs = hasLogs(logs);

  return (
    <VStack alignItems="start" gap={2} width="100%">
      {/* Status badge */}
      {isError && (
        <HStack gap={2} alignItems="center" flexWrap="wrap">
          <Badge
            size="sm"
            variant="emphasis"
            icon={<CancelCircleIcon size={10} />}
          >
            {t('failed')}
          </Badge>
        </HStack>
      )}

      {/* Result */}
      {result && (
        <VStack alignItems="start" gap={1} width="100%">
          <Text
            fontSize="xs"
            fontWeight="medium"
            color={isError ? 'fg.error' : 'fg.default'}
          >
            {'Result'}
          </Text>
          <Box
            bg="bg.canvas"
            p={2}
            borderRadius="sm"
            borderWidth="1px"
            borderColor={isError ? 'border.error' : 'border.subtle'}
            width="100%"
            maxHeight={maxHeight}
            overflow="auto"
          >
            <MarkdownPreview
              source={`\`\`\`\n${result}\n\`\`\``}
              fontSize={13}
              compact
            />
          </Box>
        </VStack>
      )}

      {/* Logs */}
      {showLogs && (
        <VStack alignItems="start" gap={1} width="100%">
          <Text fontSize="xs" fontWeight="medium" color="fg.default">
            {'Logs'}
          </Text>
          <Box
            bg="bg.canvas"
            p={2}
            borderRadius="sm"
            borderWidth="1px"
            borderColor="border.subtle"
            width="100%"
            maxHeight={maxHeight}
            overflow="auto"
          >
            <Text
              fontSize="xs"
              fontFamily="mono"
              color="fg.default"
              whiteSpace="pre-wrap"
            >
              {typeof logs === 'string' ? logs : JSON.stringify(logs, null, 2)}
            </Text>
          </Box>
        </VStack>
      )}
    </VStack>
  );
}
